import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Input from "../common/Input";
import TextArea from "../common/TextArea";
import {
  fetchCompanySeoThunk,
  updateCompanySeoThunk,
} from "../../store/thunks/companySeoThunks";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { XIcon } from "@heroicons/react/solid"; // Add this import

const mock = {
  seoTitle: "Jewelry | Fine & Fashion Jewelry | Shree Ratn",
  seoDescription:
    "Shop designer jewelry online. Find unique necklaces, rings, earrings, and bracelets. Free shipping & easy returns. Buy now & sparkle!",
  seoKeywords: ["ring", "silver", "jewelry"],
  primaryKeyword: "PRIMKEY Gold Necklace",
  secondaryKeywords: [
    "unique jewelry",
    "handmade jewelry",
    "ethical jewelry",
    "sustainable jewelry",
    "personalized jewelry",
    "custom jewelry",
  ],
  canonicalUrl: "https://demo-store.ratnsoft.com",
  faviconUrl: "",
  organizationName: "Shree Ratn",
  organizationTagline: "",
  organizationDescription: "",
  organizationLogo:
    "https://pub-51673b784cc047f5a05571d523927711.r2.dev/other-images/1736856905488_company-details",
  contactEmail: "prem@mail.com",
  contactPhone: "09004252561",
  physicalAddress: {
    street: "Anandashram C.H.S., Society No. 521, C/6",
    city: "Mumbai",
    state: "MH",
    postalCode: "400067",
    country: "India",
  },
  socialMediaLinks: {
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
  },
  openGraph: {
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    ogUrl: "",
    ogType: "",
  },
  twitterCard: {
    card: "",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
  },
  robotsMeta: {
    noindex: false,
    nofollow: false,
  },
  faqs: [],
  schemas: [],
  customScripts: [],
  storytelling: "",
  usp: "",
  _id: "678654521f1eae12a9eaf8c9",
  companyId: "67865454a43297d864d938f3",
  createdAt: "2025-01-14T12:10:58.905Z",
  updatedAt: "2025-01-14T12:23:34.256Z",
  __v: 0,
};

/**
 * A reusable collapsible section (similar to ProductSeoModal)
 */
const CollapsibleSection = ({ title, isOpenDefault = false, children }) => {
  const [isOpen, setIsOpen] = useState(isOpenDefault);

  const toggleSection = () => setIsOpen(!isOpen);

  return (
    <div className="p-4 bg-white rounded-lg border shadow-sm mb-4">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={toggleSection}
      >
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button type="button" className="text-gray-600 focus:outline-none">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>
      {isOpen && <div className="mt-4 space-y-4">{children}</div>}
    </div>
  );
};

const CompanySeoModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  // Keep track of the favicon file separately
  const [faviconFile, setFaviconFile] = useState(null);

  const {
    seoData: initialData,
    loading,
    error,
  } = useSelector((state) => state.companySeo);

  // Initialize form data from the initial data or empty
  const [seoData, setSeoData] = useState(() => ({
    // Basic SEO
    seoTitle: initialData?.seoTitle || "",
    seoDescription: initialData?.seoDescription || "",
    seoKeywords: initialData?.seoKeywords || [],
    primaryKeyword: initialData?.primaryKeyword || "",
    secondaryKeywords: initialData?.secondaryKeywords || [],
    canonicalUrl: initialData?.canonicalUrl || "",

    // Favicon
    faviconUrl: initialData?.faviconUrl || "",

    // Organization Info
    organizationName: initialData?.organizationName || "",
    organizationTagline: initialData?.organizationTagline || "",
    organizationDescription: initialData?.organizationDescription || "",
    organizationLogo: initialData?.organizationLogo || "",

    // Contact Info
    contactEmail: initialData?.contactEmail || "",
    contactPhone: initialData?.contactPhone || "",
    physicalAddress: {
      street: initialData?.physicalAddress?.street || "",
      city: initialData?.physicalAddress?.city || "",
      state: initialData?.physicalAddress?.state || "",
      postalCode: initialData?.physicalAddress?.postalCode || "",
      country: initialData?.physicalAddress?.country || "",
    },

    // Social Media Links
    socialMediaLinks: {
      facebook: initialData?.socialMediaLinks?.facebook || "",
      twitter: initialData?.socialMediaLinks?.twitter || "",
      linkedin: initialData?.socialMediaLinks?.linkedin || "",
      instagram: initialData?.socialMediaLinks?.instagram || "",
      youtube: initialData?.socialMediaLinks?.youtube || "",
    },

    // Open Graph
    openGraph: {
      ogTitle: initialData?.openGraph?.ogTitle || "",
      ogDescription: initialData?.openGraph?.ogDescription || "",
      ogImage: initialData?.openGraph?.ogImage || "",
      ogUrl: initialData?.openGraph?.ogUrl || "",
      ogType: initialData?.openGraph?.ogType || "",
    },

    // Twitter Card
    twitterCard: {
      card: initialData?.twitterCard?.card || "",
      twitterTitle: initialData?.twitterCard?.twitterTitle || "",
      twitterDescription: initialData?.twitterCard?.twitterDescription || "",
      twitterImage: initialData?.twitterCard?.twitterImage || "",
    },

    // Robots Meta
    robotsMeta: {
      noindex: initialData?.robotsMeta?.noindex || false,
      nofollow: initialData?.robotsMeta?.nofollow || false,
    },

    // FAQs
    faqs: initialData?.faqs || [],

    // Schemas
    schemas: initialData?.schemas || [],

    // Custom Scripts
    customScripts: initialData?.customScripts || [],

    // Additional SEO Content
    storytelling: initialData?.storytelling || "",
    usp: initialData?.usp || "",
  }));

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCompanySeoThunk());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (initialData) {
      setSeoData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) {
      setSeoData({
        seoTitle: "",
        seoDescription: "",
        seoKeywords: [],
        primaryKeyword: "",
        secondaryKeywords: [],
        canonicalUrl: "",
        faviconUrl: "",
        organizationName: "",
        organizationTagline: "",
        organizationDescription: "",
        organizationLogo: "",
        contactEmail: "",
        contactPhone: "",
        physicalAddress: {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
        socialMediaLinks: {
          facebook: "",
          twitter: "",
          linkedin: "",
          instagram: "",
          youtube: "",
        },
        openGraph: {
          ogTitle: "",
          ogDescription: "",
          ogImage: "",
          ogUrl: "",
          ogType: "",
        },
        twitterCard: {
          card: "",
          twitterTitle: "",
          twitterDescription: "",
          twitterImage: "",
        },
        robotsMeta: {
          noindex: false,
          nofollow: false,
        },
        faqs: [],
        schemas: [],
        customScripts: [],
        storytelling: "",
        usp: "",
      });
      setFaviconFile(null);
    }
  }, [isOpen]);

  // Handle changes for text fields, checkboxes, etc.
  const handleBasicFieldChange = (section, field, value) => {
    setSeoData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // For fields directly in seoData (e.g., seoTitle, primaryKeyword)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // For array fields (like seoKeywords, secondaryKeywords)
  const handleArrayInput = (field, rawValue) => {
    const items = rawValue
      .split(",")
      .map((i) => i.trim())
      .filter(Boolean);
    setSeoData((prev) => ({
      ...prev,
      [field]: items,
    }));
  };

  // For the favicon file input
  const handleFaviconChange = (e) => {
    setFaviconFile(e.target.files[0] || null);
  };

  // Add a handler to remove the favicon
  const handleRemoveFavicon = () => {
    setFaviconFile(null);
  };

  /********************************************************
   *    FAQS
   *******************************************************/
  const handleAddFaq = () => {
    setSeoData((prev) => ({
      ...prev,
      faqs: [
        ...prev.faqs,
        { question: "", answer: "", order: prev.faqs.length + 1 },
      ],
    }));
  };

  const handleFaqChange = (index, field, value) => {
    setSeoData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs[index] = { ...updatedFaqs[index], [field]: value };
      return { ...prev, faqs: updatedFaqs };
    });
  };

  const handleRemoveFaq = (index) => {
    setSeoData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs.splice(index, 1);
      return { ...prev, faqs: updatedFaqs };
    });
  };

  /********************************************************
   *    SCHEMAS
   *******************************************************/
  const handleAddSchema = () => {
    setSeoData((prev) => ({
      ...prev,
      schemas: [
        ...prev.schemas,
        {
          type: "Custom",
          data: "",
          order: prev.schemas.length + 1,
        },
      ],
    }));
  };

  const handleSchemaChange = (index, field, value) => {
    setSeoData((prev) => {
      const updatedSchemas = [...prev.schemas];
      updatedSchemas[index] = { ...updatedSchemas[index], [field]: value };
      return { ...prev, schemas: updatedSchemas };
    });
  };

  const handleRemoveSchema = (index) => {
    setSeoData((prev) => {
      const updatedSchemas = [...prev.schemas];
      updatedSchemas.splice(index, 1);
      return { ...prev, schemas: updatedSchemas };
    });
  };

  /********************************************************
   *    CUSTOM SCRIPTS
   *******************************************************/
  const handleAddCustomScript = () => {
    setSeoData((prev) => ({
      ...prev,
      customScripts: [...prev.customScripts, ""],
    }));
  };

  const handleCustomScriptChange = (index, value) => {
    setSeoData((prev) => {
      const updatedScripts = [...prev.customScripts];
      updatedScripts[index] = value;
      return { ...prev, customScripts: updatedScripts };
    });
  };

  const handleRemoveCustomScript = (index) => {
    setSeoData((prev) => {
      const updatedScripts = [...prev.customScripts];
      updatedScripts.splice(index, 1);
      return { ...prev, customScripts: updatedScripts };
    });
  };

  /********************************************************
   *    FORM SUBMIT
   *******************************************************/
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare formData for the API
    const formData = new FormData();
    // If user selected a new favicon file, append it
    if (faviconFile) {
      formData.append("favicon", faviconFile);
    }

    // The rest of SEO data in JSON
    formData.append("data", JSON.stringify(seoData));

    // Dispatch update
    await dispatch(updateCompanySeoThunk(formData));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title="Edit Company SEO"
      ariaLabel="Company SEO Form"
      isFullScreen
    >
      <form onSubmit={handleSubmit} className="space-y-4 p-2">
        {/* BASIC SEO */}
        <CollapsibleSection title="Basic SEO" isOpenDefault>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SEO Title"
              name="seoTitle"
              value={seoData.seoTitle}
              onChange={handleChange}
            />
            <Input
              label="Primary Keyword"
              name="primaryKeyword"
              value={seoData.primaryKeyword}
              onChange={handleChange}
            />
          </div>

          <TextArea
            label="SEO Description"
            name="seoDescription"
            value={seoData.seoDescription}
            onChange={handleChange}
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SEO Keywords (comma separated)"
              name="seoKeywords"
              value={seoData.seoKeywords.join(", ")}
              onChange={(e) => handleArrayInput("seoKeywords", e.target.value)}
            />
            <Input
              label="Secondary Keywords (comma separated)"
              name="secondaryKeywords"
              value={seoData.secondaryKeywords.join(", ")}
              onChange={(e) =>
                handleArrayInput("secondaryKeywords", e.target.value)
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Canonical URL"
              name="canonicalUrl"
              value={seoData.canonicalUrl}
              onChange={handleChange}
            />
          </div>

          {/* Favicon Upload */}
          <div className="mb-6 md:flex items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {seoData.faviconUrl ? "Update Favicon" : "Add Favicon"}
              </label>
              <input
                type="file"
                onChange={handleFaviconChange}
                accept="image/*"
                className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-gray-50 hover:file:bg-gray-100"
              />
            </div>
            {faviconFile && (
              <div className="relative mt-2">
                <img
                  src={URL.createObjectURL(faviconFile)}
                  alt="Favicon Preview"
                  className="h-8 w-8 object-cover rounded-md border"
                />
                <XIcon
                  className="absolute bg-white rounded-full -top-2 -right-2 h-6 w-6 text-red-500 cursor-pointer"
                  onClick={handleRemoveFavicon}
                />
              </div>
            )}
            {seoData.faviconUrl && !faviconFile && (
              <div className="relative mt-2 ml-2">
                <img
                  src={seoData.faviconUrl}
                  alt="Current Favicon"
                  className="h-8 w-8 object-cover rounded-md border"
                />
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">
            * The favicon often appears blurry because it is resized down to
            16x16 pixels.
          </p>
        </CollapsibleSection>

        {/* ORGANIZATION INFO */}
        <CollapsibleSection title="Organization Info">
          <Input
            label="Organization Name (Update from Company Profile)"
            name="organizationName"
            value={seoData.organizationName}
            onChange={handleChange}
            disabled
          />
          <Input
            label="Tagline"
            name="organizationTagline"
            value={seoData.organizationTagline}
            onChange={handleChange}
          />
          <TextArea
            label="Description"
            name="organizationDescription"
            rows={3}
            value={seoData.organizationDescription}
            onChange={handleChange}
          />
          <div className="border p-3 rounded-md">
            <Input
              label="Organization Logo URL (Update from Company Profile)"
              name="organizationLogo"
              value={seoData.organizationLogo}
              onChange={handleChange}
              disabled
            />
            {seoData.organizationLogo && (
              <div className="ml-2">
                <img
                  src={seoData.organizationLogo}
                  alt="Current Favicon"
                  className="h-16 w-16 object-cover rounded-md border"
                />
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* CONTACT INFO */}
        <CollapsibleSection title="Contact Info">
          <Input
            label="Contact Email"
            name="contactEmail"
            value={seoData.contactEmail}
            onChange={handleChange}
          />
          <Input
            label="Contact Phone"
            name="contactPhone"
            value={seoData.contactPhone}
            onChange={handleChange}
          />

          <div className="border p-3 rounded-md mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Physical Address
            </label>
            <Input
              label="Street"
              name="street"
              value={seoData.physicalAddress.street}
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  physicalAddress: {
                    ...prev.physicalAddress,
                    street: e.target.value,
                  },
                }))
              }
            />
            <Input
              label="City"
              name="city"
              value={seoData.physicalAddress.city}
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  physicalAddress: {
                    ...prev.physicalAddress,
                    city: e.target.value,
                  },
                }))
              }
            />
            <Input
              label="State"
              name="state"
              value={seoData.physicalAddress.state}
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  physicalAddress: {
                    ...prev.physicalAddress,
                    state: e.target.value,
                  },
                }))
              }
            />
            <Input
              label="Postal Code"
              name="postalCode"
              value={seoData.physicalAddress.postalCode}
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  physicalAddress: {
                    ...prev.physicalAddress,
                    postalCode: e.target.value,
                  },
                }))
              }
            />
            {/* <Input
              label="Country"
              name="country"
              value={seoData.physicalAddress.country}
              //   disabled
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  physicalAddress: {
                    ...prev.physicalAddress,
                    country: e.target.value,
                  },
                }))
              }
            /> */}
          </div>
        </CollapsibleSection>

        {/* SOCIAL MEDIA LINKS */}
        <CollapsibleSection title="Social Media Links">
          {Object.entries(seoData.socialMediaLinks).map(([platform, url]) => (
            <Input
              key={platform}
              label={`${platform.charAt(0).toUpperCase() + platform.slice(1)}`}
              name={platform}
              value={url}
              onChange={(e) =>
                setSeoData((prev) => ({
                  ...prev,
                  socialMediaLinks: {
                    ...prev.socialMediaLinks,
                    [platform]: e.target.value,
                  },
                }))
              }
            />
          ))}
        </CollapsibleSection>

        {/* OPEN GRAPH */}
        <CollapsibleSection title="Open Graph Metadata">
          <Input
            label="OG Title"
            name="ogTitle"
            value={seoData.openGraph.ogTitle}
            onChange={(e) =>
              handleBasicFieldChange("openGraph", "ogTitle", e.target.value)
            }
          />
          <TextArea
            label="OG Description"
            name="ogDescription"
            rows={2}
            value={seoData.openGraph.ogDescription}
            onChange={(e) =>
              handleBasicFieldChange(
                "openGraph",
                "ogDescription",
                e.target.value
              )
            }
          />
          <Input
            label="OG Image URL"
            name="ogImage"
            value={seoData.openGraph.ogImage}
            onChange={(e) =>
              handleBasicFieldChange("openGraph", "ogImage", e.target.value)
            }
          />
          <Input
            label="OG URL"
            name="ogUrl"
            value={seoData.openGraph.ogUrl}
            onChange={(e) =>
              handleBasicFieldChange("openGraph", "ogUrl", e.target.value)
            }
          />
          <Input
            label="OG Type"
            name="ogType"
            value={seoData.openGraph.ogType}
            onChange={(e) =>
              handleBasicFieldChange("openGraph", "ogType", e.target.value)
            }
          />
        </CollapsibleSection>

        {/* TWITTER CARD */}
        <CollapsibleSection title="Twitter Card Metadata">
          <Input
            label="Card Type"
            name="card"
            value={seoData.twitterCard.card}
            onChange={(e) =>
              handleBasicFieldChange("twitterCard", "card", e.target.value)
            }
            placeholder="summary or summary_large_image"
          />
          <Input
            label="Twitter Title"
            name="twitterTitle"
            value={seoData.twitterCard.twitterTitle}
            onChange={(e) =>
              handleBasicFieldChange(
                "twitterCard",
                "twitterTitle",
                e.target.value
              )
            }
          />
          <TextArea
            label="Twitter Description"
            name="twitterDescription"
            rows={2}
            value={seoData.twitterCard.twitterDescription}
            onChange={(e) =>
              handleBasicFieldChange(
                "twitterCard",
                "twitterDescription",
                e.target.value
              )
            }
          />
          <Input
            label="Twitter Image"
            name="twitterImage"
            value={seoData.twitterCard.twitterImage}
            onChange={(e) =>
              handleBasicFieldChange(
                "twitterCard",
                "twitterImage",
                e.target.value
              )
            }
          />
        </CollapsibleSection>

        {/* ROBOTS META */}
        <CollapsibleSection title="Robots Meta">
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={seoData.robotsMeta.noindex}
                onChange={(e) =>
                  setSeoData((prev) => ({
                    ...prev,
                    robotsMeta: {
                      ...prev.robotsMeta,
                      noindex: e.target.checked,
                    },
                  }))
                }
              />
              <span className="ml-2">Noindex</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={seoData.robotsMeta.nofollow}
                onChange={(e) =>
                  setSeoData((prev) => ({
                    ...prev,
                    robotsMeta: {
                      ...prev.robotsMeta,
                      nofollow: e.target.checked,
                    },
                  }))
                }
              />
              <span className="ml-2">Nofollow</span>
            </label>
          </div>
        </CollapsibleSection>

        {/* FAQS */}
        <CollapsibleSection title="FAQs">
          <div className="space-y-4">
            {seoData.faqs.map((faqItem, index) => (
              <div key={index} className="border p-3 rounded-md relative">
                <div className="absolute right-2 top-2 flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveFaq(index)}
                  >
                    Remove
                  </Button>
                </div>

                <Input
                  label="Question"
                  name="question"
                  value={faqItem.question}
                  onChange={(e) =>
                    handleFaqChange(index, "question", e.target.value)
                  }
                  required
                />
                <TextArea
                  label="Answer"
                  name="answer"
                  rows={2}
                  value={faqItem.answer}
                  onChange={(e) =>
                    handleFaqChange(index, "answer", e.target.value)
                  }
                  required
                />
                <Input
                  label="Order"
                  type="number"
                  name="order"
                  value={faqItem.order || ""}
                  onChange={(e) =>
                    handleFaqChange(index, "order", e.target.value)
                  }
                />
              </div>
            ))}

            <Button type="button" variant="secondary" onClick={handleAddFaq}>
              Add FAQ
            </Button>
          </div>
        </CollapsibleSection>

        {/* SCHEMAS */}
        <CollapsibleSection title="Schema Markups">
          <div className="space-y-4">
            {seoData.schemas.map((schemaItem, index) => (
              <div key={index} className="border p-3 rounded-md relative">
                <div className="absolute right-2 top-2 flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveSchema(index)}
                  >
                    Remove
                  </Button>
                </div>

                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Schema Type
                  </label>
                  <select
                    className="border border-gray-300 rounded p-2 w-full"
                    value={schemaItem.type}
                    onChange={(e) =>
                      handleSchemaChange(index, "type", e.target.value)
                    }
                  >
                    <option value="Product">Product</option>
                    <option value="FAQ">FAQ</option>
                    <option value="Review">Review</option>
                    <option value="Organization">Organization</option>
                    <option value="LocalBusiness">LocalBusiness</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Schema Data (JSON or text)
                  </label>
                  <textarea
                    rows={4}
                    className="border border-gray-300 rounded p-2 w-full"
                    value={
                      typeof schemaItem.data === "string"
                        ? schemaItem.data
                        : JSON.stringify(schemaItem.data, null, 2)
                    }
                    onChange={(e) =>
                      handleSchemaChange(index, "data", e.target.value)
                    }
                  />
                </div>

                <Input
                  label="Order"
                  type="number"
                  name="order"
                  value={schemaItem.order || ""}
                  onChange={(e) =>
                    handleSchemaChange(index, "order", e.target.value)
                  }
                />
              </div>
            ))}

            <Button type="button" variant="secondary" onClick={handleAddSchema}>
              Add Schema
            </Button>
          </div>
        </CollapsibleSection>

        {/* CUSTOM SCRIPTS */}
        <CollapsibleSection title="Custom Scripts">
          <p className="text-gray-500 text-sm mb-2">
            You can embed raw script tags or code snippets here.
          </p>
          <div className="space-y-4">
            {seoData.customScripts.map((script, index) => (
              <div key={index} className="border p-3 rounded-md relative">
                <div className="absolute right-2 top-2 flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveCustomScript(index)}
                  >
                    Remove
                  </Button>
                </div>
                <TextArea
                  label={`Script #${index + 1}`}
                  name="customScripts"
                  rows={4}
                  value={script}
                  onChange={(e) =>
                    handleCustomScriptChange(index, e.target.value)
                  }
                />
              </div>
            ))}

            <Button
              type="button"
              variant="secondary"
              onClick={handleAddCustomScript}
            >
              Add Custom Script
            </Button>
          </div>
        </CollapsibleSection>

        {/* STORYTELLING & USP */}
        <CollapsibleSection title="Storytelling & USP">
          <TextArea
            label="Storytelling"
            name="storytelling"
            rows={3}
            value={seoData.storytelling}
            onChange={handleChange}
          />
          <TextArea
            label="Unique Selling Proposition (USP)"
            name="usp"
            rows={3}
            value={seoData.usp}
            onChange={handleChange}
          />
        </CollapsibleSection>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end pt-2 gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(null)}
          >
            Cancel
          </Button>
          <Button disabled={loading} type="submit" variant="primary">
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CompanySeoModal;
